import React from "react";

export type TourneeHealthData = {
  totalPassages: number;
  completedPassages: number;
  pendingPassages: number;
  failedPassages: number;
  progressPercent: number;
  capacityMax: number | null;
  capacityUsed: number;
  capacityRemaining: number | null;
  overload: boolean;
  lateCount: number;
  urgentCount: number;
  slaRiskCount: number;
  pendingValidationCount?: number;
  healthStatus: "OK" | "WARNING" | "CRITICAL";
  reasons: string[];
};

function TourneeHealthCardComponent({ health }: { health: TourneeHealthData }): React.JSX.Element {
  const header =
    health.healthStatus === "OK"
      ? { icon: "✓", color: "text-emerald-200", message: "Tournée sous contrôle" }
      : health.healthStatus === "WARNING"
        ? { icon: "!", color: "text-amber-200", message: health.reasons[0] ?? "Points à surveiller" }
        : { icon: "⚠", color: "text-rose-200", message: "Action superviseur requise" };
  const tone =
    health.healthStatus === "OK"
      ? "border-emerald-500/30 text-emerald-200"
      : health.healthStatus === "WARNING"
        ? "border-amber-500/30 text-amber-200"
        : "border-rose-500/30 text-rose-200";

  return (
    <section className={`rounded-2xl border bg-slate-900/40 p-4 ${tone}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-white">Santé tournée</h3>
        <span className={`rounded-full bg-slate-800 px-2 py-0.5 text-xs font-semibold ${header.color}`}>
          {header.icon} {health.healthStatus}
        </span>
      </div>
      <p className={`mt-2 text-xs font-medium ${header.color}`}>{header.message}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
        <Stat label="Progression" value={`${health.progressPercent}%`} />
        <Stat label="Capacité" value={`${health.capacityUsed}/${health.capacityMax ?? "—"}`} />
        <Stat label="Retards" value={String(health.lateCount)} />
        <Stat label="Urgences" value={String(health.urgentCount)} />
        <Stat label="SLA risk" value={String(health.slaRiskCount)} />
        <Stat
          label="Validations"
          value={String(health.pendingValidationCount ?? 0)}
        />
        <Stat label="Restants" value={String(health.pendingPassages)} />
      </div>
      {health.reasons.length > 0 ? (
        <ul className="mt-3 space-y-1 text-xs text-slate-300">
          {health.reasons.slice(0, 3).map((r) => (
            <li key={r}>- {r}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

const TourneeHealthCard = React.memo(TourneeHealthCardComponent);
export default TourneeHealthCard;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-2 py-1.5">
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-white">{value}</p>
    </div>
  );
}
